import { type MouseEvent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { SORT_BY } from '@eshop/type'
import { useQuery } from '@tanstack/react-query'

import css from './Eshop.module.css'

import { fetchRentalFilters } from '#/local/repository/Filters'
import { fetchProductCodesByFilters, fetchProductCodesByQuery, fetchProductsRental } from '#/local/repository/Product'
import { sendReport } from '#/local/repository/Report'

import { AddToCartRental } from '#/global/component/Product/AddToCart/Rental'
import { LayoutGrid } from '#/global/component/Product/LayoutGrid'
import { PriceRental } from '#/global/component/Product/Price/Rental'
import { type FiltersSelectedValues, ProductFilters } from '#/global/component/Product/ProductFilters'
import { SelectedFilters } from '#/global/component/Product/ProductFilters/SelectedFilters'
import { ProductPreviewLong } from '#/global/component/Product/ProductPreviewLong'
import { Sticker } from '#/global/component/Product/Sticker'

import { Config } from '#/global/helper/Config'
import { linkToProductRental } from '#/global/helper/LinkTo'
import { MessageFlash } from '#/global/helper/MessageFlash'

export const Rental = () => {
    const lang = Config.getLang()
    const [searchParameters, setSearchParameters] = useSearchParams()
    const [filtersValues, setFiltersValues] = useState<FiltersSelectedValues<'PRICE'>>({ PRICE: [] })

    const query = searchParameters.get('query')
    const page = Number(searchParameters.get('page')) || 1
    const sortBy = (searchParameters.get('sortBy') as SORT_BY) ?? 'DEFAULT'

    // Fetch original product codes (All product codes without filtering. Max 240 items.)
    const productCodesOrigin = useQuery({
        queryKey: ['rental productCodesOrigin', { query }],
        queryFn: async () => fetchProductCodesByQuery(query!, 'rental'),
        enabled: !!query,
    })

    /**
     * Fetch original filters
     */
    const filtersOrigin = useQuery({
        queryKey: ['rental filters', { productCodesOrigin: productCodesOrigin.data }],
        queryFn: async () => fetchRentalFilters(productCodesOrigin.data!, {}),
        enabled: productCodesOrigin.isSuccess,
    })

    /**
     * Fetch filtered product codes
     */
    const productCodesFiltered = useQuery({
        queryKey: ['rental productCodesFiltered', { filtersValues, sortBy, productCodesOrigin: productCodesOrigin.data }],
        queryFn: async () => fetchProductCodesByFilters(productCodesOrigin.data!, filtersValues, sortBy, 'rental'),
        enabled: productCodesOrigin.isSuccess,
    })

    /**
     * Fetch products by page
     */
    const products = useQuery({
        queryKey: ['rental products', { productCodesFiltered: productCodesFiltered.data, page }],
        queryFn: async () => fetchProductsRental(productCodesFiltered.data!, page),
        placeholderData: previousData =>
            previousData ?? (Array.from({ length: 12 })),
        enabled: productCodesFiltered.isSuccess,
    })

    // Page change
    const setPage = (page: number) => {
        const urlParameters = new URLSearchParams(window.location.search)
        urlParameters.set('page', page.toString())
        setSearchParameters(urlParameters)
    }

    // Sort change
    const setSortBy = (sortBy: SORT_BY) => {
        const urlParameters = new URLSearchParams(window.location.search)
        urlParameters.set('sortBy', sortBy.toString())
        setSearchParameters(urlParameters)
    }

    // Filter change
    const handleFilterChange = (filterId: string, selected: string[] | number[]) => {
        if (filterId === 'ALL') {
            setFiltersValues({ PRICE: [] })
        } else {
            setFiltersValues({ ...filtersValues, [filterId]: selected })
        }
        setPage(1)
    }

    if (productCodesOrigin.isError || filtersOrigin.isError || productCodesFiltered.isError || products.isError) {
        MessageFlash.add({
            message: 'Nastala chyba při načítání produktů.',
            type: 'error',
        })
    }

    const handleSendReport = (event: MouseEvent<HTMLAnchorElement>, { code, slug }: { code: string, slug: string }) => {
        event.preventDefault()
        const link = linkToProductRental(code, slug)
        query && void sendReport(query, 'products-rental', code, link)
    }

    return (
        <div className={css.container}>
            {filtersOrigin.isSuccess
                ? (
                        <ProductFilters
                            filters={filtersOrigin.data}
                            handleChange={handleFilterChange}
                            lang={lang}
                            values={filtersValues}
                        />
                    )
                : <ProductFilters loadingState />}
            <div>
                {filtersValues.PRICE !== undefined && filtersOrigin.isSuccess && (
                    <SelectedFilters
                        filters={filtersOrigin.data}
                        handleChange={handleFilterChange}
                        lang={lang}
                        values={filtersValues}
                    />
                )}
                <LayoutGrid
                    lang={lang}
                    loadingState={!products.isSuccess || !productCodesFiltered.isSuccess}
                    page={page}
                    productsCount={productCodesFiltered.data?.length ?? 0}
                    setPage={setPage}
                    setSortBy={setSortBy}
                    sortBy={sortBy}>
                    {products.data?.map((product, index) =>
                        product
                            ? (
                                    <ProductPreviewLong
                                        key={product.productId}
                                        AdditionalInfo={<Sticker product={product} />}
                                        AddToCart={<AddToCartRental lang={lang} product={product} type="long" />}
                                        loadingState={!products.isSuccess}
                                        onClick={event => handleSendReport(event, { code: product.groupCode, slug: product.detail.slug })}
                                        product={product}
                                        ProductPrice={<PriceRental lang={lang} product={product} />}
                                    />
                                )
                            : <ProductPreviewLong key={index} loadingState />,
                    )}
                </LayoutGrid>
            </div>
        </div>
    )
}
